import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchIssues, fetchRecentPapers, downloadPaper } from '../utils/api';
import CitationModal from '../components/CitationModal';
import '../styles/PageStyles.css';

const CurrentPublications = () => {
  const [currentIssue, setCurrentIssue] = useState(null);
  const [recentArticles, setRecentArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [showCitationModal, setShowCitationModal] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const articlesPerPage = 10;

  useEffect(() => {
    loadPublications();
  }, []);

  const loadPublications = async () => {
    try {
      setLoading(true);
      const [issuesData, papersData] = await Promise.all([
        fetchIssues(),
        fetchRecentPapers()
      ]);

      // Set the current issue (look for isCurrent flag, or use most recent)
      if (Array.isArray(issuesData) && issuesData.length > 0) {
        const current = issuesData.find(issue => issue.isCurrent) || issuesData[0];
        setCurrentIssue(current);
      }

      // Ensure papers data is an array and has proper structure
      let processedPapers = [];
      if (Array.isArray(papersData)) {
        processedPapers = papersData.map(paper => ({
          ...paper,
          // Ensure keywords is properly formatted
          keywords: paper.keywords || '',
          // Ensure authors is properly formatted
          authors: paper.authors || []
        }));
      }

      setRecentArticles(processedPapers);
    } catch (err) {
      console.error('Error loading publications:', err);
      setError('Unable to load publications at this time.');
      setRecentArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      // Simulate subscription
      setSubscribed(true);
      setTimeout(() => setSubscribed(false), 3000);
      setEmail('');
    }
  };

  const handleDownload = async (paperId) => {
    try {
      setDownloadingId(paperId);
      await downloadPaper(paperId);
    } catch (err) {
      console.error('Error downloading paper:', err);
      alert('Failed to download paper. Please try again later.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleCite = (paper) => {
    setSelectedPaper(paper);
    setShowCitationModal(true);
  };

  // Using real data from API with pagination
  const totalArticles = recentArticles.length;
  const indexOfLastArticle = currentPage * articlesPerPage;
  const indexOfFirstArticle = indexOfLastArticle - articlesPerPage;
  const displayArticles = recentArticles.slice(indexOfFirstArticle, indexOfLastArticle);

  // Change page
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 300, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading current publications...</p>
      </div>
    );
  }

  return (
    <>
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1 className="page-title">Current Publications</h1>
          <p className="page-subtitle">
            Explore the latest research articles published in FUWJALS, featuring cutting-edge
            studies in agriculture and life sciences.
          </p>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="breadcrumb-nav">
        <div className="container">
          <nav className="breadcrumb">
            <span className="breadcrumb-item">
              <Link to="/">Home</Link>
            </span>
            <span className="breadcrumb-item">
              <i className="fas fa-chevron-right"></i>
            </span>
            <span className="breadcrumb-item active">Current Publications</span>
          </nav>
        </div>
      </div>

      <div className="container">
        <div className="row">
          <div className="col-lg-8">
            {/* Current Issue Card */}
            <div className="publication-card" style={{ '--card-index': 0 }}>
              <div className="publication-header">
                <div>
                  <h2 className="publication-title">
                    <i className="fas fa-book-open text-primary me-2"></i>
                    {currentIssue ? (
                      `Latest Issue - Volume ${currentIssue.volume}, Issue ${currentIssue.issue}`
                    ) : (
                      'Loading Latest Issue...'
                    )}
                  </h2>
                  <div className="publication-meta">
                    <span className="meta-item">
                      <i className="fas fa-calendar-alt"></i>
                      {currentIssue?.month || ''} {currentIssue?.year || ''}
                    </span>
                    <span className="meta-item">
                      <i className="fas fa-file-alt"></i>
                      {currentIssue?.papers?.length || 0} Articles
                    </span>
                    <span className="meta-item">
                      <i className="fas fa-calendar-check"></i>
                      Published: {currentIssue?.publicationDate ? new Date(currentIssue.publicationDate).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>
                <div>
                  <span className="badge bg-success">Current Issue</span>
                </div>
              </div>
              <p className="publication-abstract">
                {currentIssue?.description || 'Browse the latest research articles in this issue.'}
              </p>
              <div className="publication-actions">
                <Link to={`/issues/${currentIssue?.id}`} className="btn btn-primary">
                  <i className="fas fa-eye me-2"></i>
                  View Full Issue
                </Link>
                {currentIssue?.pdfUrl && (
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => {
                      window.open(`/api/public/issues/${currentIssue.id}/download`, '_blank');
                    }}
                  >
                    <i className="fas fa-download me-2"></i>
                    Download PDF
                  </button>
                )}
              </div>
            </div>

            {/* Featured Articles */}
            <h3 className="mb-4">Featured Articles</h3>
            {displayArticles.map((article, index) => (
              <div
                key={article.id}
                className="publication-card"
                style={{ '--card-index': index + 1 }}
              >
                <h4 className="publication-title">{article.title}</h4>
                <div className="publication-meta">
                  <span className="meta-item">
                    <i className="fas fa-users"></i>
                    {Array.isArray(article.authors) ?
                      article.authors.map(a => typeof a === 'object' ? a.name : a).join(', ') :
                      article.authors || 'Unknown Authors'}
                  </span>
                </div>
                <p className="publication-abstract">{article.abstract}</p>

                {article.keywords && (
                  <div className="publication-keywords">
                    {typeof article.keywords === 'string' ? (
                      article.keywords.split(',').map((keyword, idx) => (
                        <span key={idx} className="keyword-badge">{keyword.trim()}</span>
                      ))
                    ) : Array.isArray(article.keywords) ? (
                      article.keywords.map((keyword, idx) => (
                        <span key={idx} className="keyword-badge">{keyword}</span>
                      ))
                    ) : null}
                  </div>
                )}

                <div className="publication-meta mb-3">
                  <span className="meta-item">
                    <i className="fas fa-bookmark"></i>
                    Pages: {article.pages}
                  </span>
                  {article.doi && (
                    <span className="meta-item">
                      <i className="fas fa-fingerprint"></i>
                      DOI: {article.doi}
                    </span>
                  )}
                  {article.downloads && (
                    <span className="meta-item">
                      <i className="fas fa-download"></i>
                      {article.downloads} downloads
                    </span>
                  )}
                  {article.citations && (
                    <span className="meta-item">
                      <i className="fas fa-quote-right"></i>
                      {article.citations} citations
                    </span>
                  )}
                </div>

                <div className="publication-actions">
                  <Link
                    to={`/paper/${article.id}`}
                    state={{ from: '/current-publications', fromLabel: 'Current Publications' }}
                    className="btn btn-sm btn-primary"
                  >
                    <i className="fas fa-eye me-2"></i>
                    View Details
                  </Link>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => handleDownload(article.id)}
                    disabled={downloadingId === article.id}
                  >
                    {downloadingId === article.id ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Downloading...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-file-pdf me-2"></i>
                        Download PDF
                      </>
                    )}
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => handleCite(article)}
                  >
                    <i className="fas fa-quote-left me-2"></i>
                    Cite
                  </button>
                </div>
              </div>
            ))}

            {/* Pagination */}
            {totalArticles > articlesPerPage && (
              <nav className="mt-5">
                <ul className="pagination justify-content-center">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                  </li>
                  
                  {Array.from({ length: Math.ceil(totalArticles / articlesPerPage) }, (_, i) => (
                    <li 
                      key={i + 1} 
                      className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}
                    >
                      <button 
                        className="page-link"
                        onClick={() => paginate(i + 1)}
                      >
                        {i + 1}
                      </button>
                    </li>
                  ))}
                  
                  <li className={`page-item ${currentPage === Math.ceil(totalArticles / articlesPerPage) ? 'disabled' : ''}`}>
                    <button 
                      className="page-link"
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === Math.ceil(totalArticles / articlesPerPage)}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            )}

            <div className="text-center mt-5">
              <Link to="/archives" className="btn btn-lg btn-primary">
                <i className="fas fa-archive me-2"></i>
                Browse All Publications
              </Link>
            </div>
          </div>

          <div className="col-lg-4">
            {/* Issue Statistics - Commented out as requested
            <div className="info-card mb-4">
              <div className="info-card-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <h3 className="info-card-title">Issue Statistics</h3>
              <div className="row g-3 mt-3">
                <div className="col-6">
                  <div className="stats-card">
                    <div className="stats-number">452</div>
                    <div className="stats-label">Total Views</div>
                  </div>
                </div>
                <div className="col-6">
                  <div className="stats-card">
                    <div className="stats-number">128</div>
                    <div className="stats-label">Downloads</div>
                  </div>
                </div>
                <div className="col-6">
                  <div className="stats-card">
                    <div className="stats-number">3.2</div>
                    <div className="stats-label">Impact Factor</div>
                  </div>
                </div>
                <div className="col-6">
                  <div className="stats-card">
                    <div className="stats-number">15</div>
                    <div className="stats-label">Countries</div>
                  </div>
                </div>
              </div>
            </div>
            */}

            {/* Upcoming Issue (Dynamic if available, else hidden or generic) */}
            {currentIssue && (
              <div className="info-card mb-4">
                <div className="info-card-icon">
                  <i className="fas fa-calendar-plus"></i>
                </div>
                <h3 className="info-card-title">Next Issue</h3>
                <p className="info-card-text">
                  <strong>Volume {currentIssue.volume}, Issue {currentIssue.issue + 1}</strong><br />
                  Expected soon. Stay tuned for updates!
                </p>
                <p className="info-card-text mt-3">
                  Check our announcements for call for papers.
                </p>
                <Link to="/submit-paper" className="btn btn-sm btn-primary mt-3">
                  Submit Your Paper
                </Link>
              </div>
            )}

            {/* Email Subscription */}
            <div className="form-modern">
              <h3 className="mb-3">
                <i className="fas fa-envelope me-2 text-primary"></i>
                Subscribe to Updates
              </h3>
              <p className="text-secondary mb-4">
                Get notified when new issues are published
              </p>
              <form onSubmit={handleSubscribe}>
                <div className="form-group-modern">
                  <label className="form-label-modern">Email Address</label>
                  <input
                    type="email"
                    className="form-control-modern"
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <small className="form-text">We respect your privacy</small>
                </div>
                <button type="submit" className="btn btn-primary w-100">
                  <i className="fas fa-bell me-2"></i>
                  Subscribe
                </button>
                {subscribed && (
                  <div className="alert alert-success mt-3 fade show">
                    <i className="fas fa-check-circle me-2"></i>
                    Successfully subscribed!
                  </div>
                )}
              </form>
            </div>

            {/* Quick Links */}
            <div className="info-card">
              <h3 className="info-card-title">Quick Links</h3>
              <div className="list-group list-group-flush">
                <Link to="/archives" className="list-group-item list-group-item-action">
                  <i className="fas fa-archive me-2 text-primary"></i>
                  Archive Issues
                </Link>
                <Link to="/special-issues" className="list-group-item list-group-item-action">
                  <i className="fas fa-star me-2 text-warning"></i>
                  Special Issues
                </Link>
                <Link to="/search" className="list-group-item list-group-item-action">
                  <i className="fas fa-search me-2 text-info"></i>
                  Search Articles
                </Link>
                <Link to="/guide-for-authors" className="list-group-item list-group-item-action">
                  <i className="fas fa-pen me-2 text-success"></i>
                  Author Guidelines
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Citation Modal */}
      {showCitationModal && selectedPaper && (
        <CitationModal
          paper={selectedPaper}
          onClose={() => {
            setShowCitationModal(false);
            setSelectedPaper(null);
          }}
        />
      )}
    </>
  );
};

export default CurrentPublications;